import { CourseApi, KlassApi } from '@app/api';

/**
 * Where a record named by the feed can be followed to.
 *
 * Three answers, because the registry has three kinds of entity in it and they are not
 * interchangeable - see README-history.md in the allocation service:
 *
 * - `record` - it has a page of its own, addressed by this row.
 * - `list` - the row *is* the record and its only view is a table, so the honest destination is
 *   that table rather than a page for the row. The reader still has to find the row on it, which is
 *   why this is distinguished rather than dressed up as the first case.
 * - `none` - part of a larger record: registered for the feed, with no resource and no page. There
 *   is nothing to navigate to and offering a link would be a lie.
 */
export type RecordTarget =
  | { kind: 'record'; path: string }
  | { kind: 'list'; path: string; listLabel: string }
  | { kind: 'none' };

const NONE: RecordTarget = { kind: 'none' };

/**
 * The slugs that have a page, and what to do with an id to reach it.
 *
 * Keyed by slug rather than by the Java type, because the slug is what the feed reports and what
 * the history endpoint is addressed by. A slug missing from here answers `none`, which is the right
 * default twice over: the parts of a larger record genuinely have no page, and an entity registered
 * on the service side later should read as "nothing to follow" until somebody gives it one, not as
 * a broken link.
 */
const TARGETS: Record<string, (id: number) => RecordTarget | Promise<RecordTarget>> = {
  // Addressed by something other than the id, so the id has to be exchanged for it. Done on the
  // click rather than for every row of every page - a page of 25 revisions would otherwise be 25
  // extra requests to render chips most of which are never followed.
  class: async id => {
    const klass = await KlassApi.getById(id);
    return klass?.offeringId ? { kind: 'record', path: `/classes/${klass.offeringId}` } : NONE;
  },
  course: async id => {
    const course = await CourseApi.getById(id);
    return course?.sku ? { kind: 'record', path: `/courses/${course.sku}` } : NONE;
  },

  // Addressed by the id, like the audit trail itself
  instructor: id => ({ kind: 'record', path: `/instructors/${id}` }),
  facility: id => ({ kind: 'record', path: `/facilities/${id}` }),
  partner: id => ({ kind: 'record', path: `/partners/${id}` }),
  clientexecutive: id => ({ kind: 'record', path: `/clientexecutives/${id}` }),
  tss: id => ({ kind: 'record', path: `/tss/${id}` }),
  pipelineclass: id => ({ kind: 'record', path: `/classes/pipeline/${id}` }),
  flagdefinition: id => ({ kind: 'record', path: `/admin/flags/${id}` }),

  // The row is a table row, and the table is the only place it is shown
  privatecourse: () => ({ kind: 'list', path: '/privatecourses', listLabel: 'Private Courses' }),
  redhatregion: () => ({ kind: 'list', path: '/admin/redhatregions', listLabel: 'Red Hat Regions' }),
  customfield: () => ({ kind: 'list', path: '/customfields', listLabel: 'Custom Fields' }),

  /*
   * Settings, where the row id is not the thing worth addressing: a reset deletes the row and
   * setting the value again inserts a new one, so an id identifies one *incarnation* of a key
   * rather than the setting. Their own pages carry the key-addressed history, which is the
   * canonical one for these two - the feed exists here to say a change happened and who caused it.
   */
  parameters: () => ({ kind: 'list', path: '/admin/general', listLabel: 'General Settings' }),
  communications: () => ({ kind: 'list', path: '/admin/communications', listLabel: 'Communications' })
};

/** Whether a chip for this slug leads anywhere, decided without a request. */
export const hasTarget = (entity: string): boolean => entity in TARGETS;

/** Where to send a reader who follows one record out of the feed. */
export const targetFor = async (entity: string, id: number): Promise<RecordTarget> => {
  const resolve = TARGETS[entity];
  return resolve ? resolve(id) : NONE;
};
